import { useCallback, useId, type ComponentPropsWithRef, type ReactNode } from "react";

import { cn } from "../lib/cn";

export interface CheckboxProps extends Omit<ComponentPropsWithRef<"input">, "type"> {
  /** Libellé cliquable rendu à droite de la case. */
  label?: ReactNode;
  /** Précision affichée sous le libellé. */
  description?: ReactNode;
  /**
   * Troisième état : « certains, mais pas tous ». Indispensable pour une case
   * de tête qui commande une liste.
   *
   * Ce n'est pas un attribut HTML mais une PROPRIÉTÉ du DOM : elle n'existe
   * qu'à l'exécution et React ne la pose pas depuis le JSX. D'où le passage
   * par une ref — sans quoi la case afficherait « décoché » alors que
   * l'accessibilité annoncerait, elle, l'état mixte.
   */
  indeterminate?: boolean;
}

/**
 * Case à cocher.
 *
 * L'`<input>` natif est conservé (il apporte le clavier, les formulaires et
 * l'état indéterminé) mais rendu invisible via `appearance-none`. La coche est
 * dessinée par-dessus et révélée par la variante `peer-checked`.
 * C'est un contrôle non contrôlé par défaut : passez `checked` + `onChange`
 * pour le piloter.
 */
export function Checkbox({
  className,
  label,
  description,
  id,
  indeterminate,
  ref,
  ...props
}: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  // Ref combinée : on doit écrire la propriété `indeterminate` sur le nœud sans
  // pour autant confisquer la ref que l'appelant a pu passer.
  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) node.indeterminate = Boolean(indeterminate);

      if (typeof ref === "function") ref(node);
      else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
    },
    [indeterminate, ref],
  );

  const box = (
    <span className="relative inline-flex shrink-0 items-center justify-center">
      <input
        type="checkbox"
        id={inputId}
        ref={setRef}
        aria-checked={indeterminate ? "mixed" : undefined}
        className={cn(
          "peer size-4 cursor-pointer appearance-none rounded-md border border-border-strong bg-surface-raised shadow-xs inset-shadow-control touch-manipulation",
          "before:absolute before:-inset-3 before:content-[''] sm:before:hidden",
          "transform-gpu transition-[background-color,border-color,transform] duration-fast ease-spring",
          "checked:border-accent checked:bg-accent checked:inset-shadow-raised",
          "indeterminate:border-accent indeterminate:bg-accent",
          "active:scale-90 active:duration-instant",
          "hover:border-fg-muted checked:hover:border-accent",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-1 focus-visible:ring-offset-surface",
          "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-50 disabled:active:scale-100",
          className,
        )}
        {...props}
      />
      {/*
        La coche se TRACE au lieu d'apparaître. `stroke-dasharray` masque le
        tracé sur toute sa longueur, et le focus fait revenir l'offset à zéro :
        le trait se dessine de gauche à droite.
        On anime via une transition et non une keyframe, pour que le tracé se
        rétracte proprement au décochage — une animation ne joue que dans un sens.
      */}
      <svg
        className={cn(
          "pointer-events-none absolute size-3 text-fg-on-accent",
          "[stroke-dasharray:18] [stroke-dashoffset:18] opacity-0",
          "transition-[stroke-dashoffset,opacity] duration-normal ease-standard",
          "peer-checked:opacity-100 peer-checked:[stroke-dashoffset:0]",
        )}
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m2.5 6.5 2.5 2.5 4.5-5.5" />
      </svg>

      {/* Le trait de l'état mixte. Un fond plein sans marque se lit comme
          « coché » : l'état indéterminé a besoin de son propre signe. */}
      <span
        className="pointer-events-none absolute h-0.5 w-2 scale-x-0 rounded-full bg-fg-on-accent opacity-0 transition-[transform,opacity] duration-fast ease-standard peer-indeterminate:scale-x-100 peer-indeterminate:opacity-100"
        aria-hidden="true"
      />
    </span>
  );

  if (!label) return box;

  return (
    <div className="flex items-start gap-2.5">
      <span className="flex h-5 items-center">{box}</span>
      <span className="flex flex-col gap-0.5">
        <label
          htmlFor={inputId}
          className="cursor-pointer text-sm text-fg select-none has-disabled:cursor-not-allowed"
        >
          {label}
        </label>
        {description && <span className="text-xs text-fg-muted">{description}</span>}
      </span>
    </div>
  );
}
